import { esc } from '../escape.js';
import { formatMoney, currencySymbol } from '../currency.js';
import { CARDS, TILES, SWAP_ICON, swapButton } from '../homeCards.js';
import { tableStatus } from './tablesView.js';
import { zonedParts } from '../scheduleTime.js';

// Swappable home cards + stat tiles (homeCards.js has the lists and the
// per-device prefs). The two built-in cards (the left column's Getting
// Started / Activity / My shift card and the Active Queue panel) are MOVED
// between the columns, never re-rendered, so their own views and handlers keep
// working. Every other card is drawn into one of two spare slot elements.
// Tiles work the same way: the three original tiles stay in index.html (the
// dashboard count-ups write to their ids) and extra tiles are drawn here; CSS
// `order` puts the chosen three in place.

const STATIC_TILES = { sales: '.home-sales-stat', txns: '#transactionsCard', serving: '.home-dash-stat--serving' };
const TILE_LOOK = {
  avgOrder:    { emoji: '🧾', accent: 'sales',   title: 'View sales report' },
  tips:        { emoji: '💵', accent: 'sales',   title: 'View sales report' },
  itemsSold:   { emoji: '🍽️', accent: 'sales',   title: 'View sales report' },
  lastWeek:    { emoji: '📈', accent: 'sales',   title: 'View sales report' },
  expenses:    { emoji: '💸', accent: 'sales',   title: 'Open Sales & Expenses' },
  waiting:     { emoji: '⏳', accent: 'kitchen', title: 'Open the Active Queue' },
  onShift:     { emoji: '👥', accent: 'kitchen', title: "See who's working" },
  requests:    { emoji: '📬', accent: 'kitchen', title: 'Review requests' },
  freeTables:  { emoji: '🪑', accent: 'tables',  title: 'Open Tables' },
  openTabs:    { emoji: '📋', accent: 'tables',  title: 'Open Tables' },
  nextBooking: { emoji: '📅', accent: 'tables',  title: 'Open reservations' },
  lowItems:    { emoji: '📦', accent: 'stock',   title: 'Open Inventory' },
};

const svg = (d, s = 18) => `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const CARD_ICONS = {
  main:         '<path d="M22 12h-4l-3 9L9 3l-3 9H2"/>',
  queue:        '<path d="M9 11l3 3 8-8"/><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9"/>',
  tables:       '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="3.5"/><rect x="3" y="14" width="7" height="7" rx="3.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>',
  reservations: '<path d="M8 2v4"/><path d="M16 2v4"/><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M3 10h18"/><path d="m9 16 2 2 4-4"/>',
  waitlist:     '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  team:         '<path d="M16 21v-2a4 4 0 0 0-8 0v2"/><circle cx="12" cy="7" r="4"/>',
  topSellers:   '<path d="M3 3v18h18"/><path d="m7 15 4-4 3 3 6-6"/>',
  lowStock:     '<path d="M21 8 12 3 3 8v8l9 5 9-5z"/><path d="m3 8 9 5 9-5"/><path d="M12 13v8"/>',
  salesToday:   '<path d="M4 20V10"/><path d="M10 20V4"/><path d="M16 20v-7"/><path d="M22 20H2"/>',
  salesWeek:    '<rect x="3" y="4" width="18" height="17" rx="2"/><path d="M3 9h18"/><path d="M8 14v3"/><path d="M12 12v5"/><path d="M16 15v2"/>',
  schedule:     '<rect x="3" y="4" width="18" height="17" rx="2"/><path d="M3 9h18"/><path d="M8 2v4"/><path d="M16 2v4"/><path d="M7 13h4"/><path d="M7 17h7"/>',
  inbox:        '<path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.5 5h13L22 12v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6z"/>',
  purchaseOrders: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M8 13h8"/><path d="M8 17h5"/>',
};

const mins = (ms) => Math.max(0, Math.floor(ms / 60000));
const ago = (iso, now) => {
  const m = mins(now - new Date(iso).getTime());
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
};
// Business-timezone clock (set from renderCards data), like the Tables panel.
let TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const clock = (d) => {
  const { h, mi: m } = zonedParts(new Date(d), TZ);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
const initials = (name) => (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
// Whole units for bar labels ("$1,240", "$12.3k"); the exact amount is in the bar's title.
const shortMoney = (n) => {
  const v = Math.round(Number(n) || 0);
  return v >= 10000 ? `${currencySymbol()}${(v / 1000).toFixed(1).replace(/\.0$/, '')}k` : `${currencySymbol()}${v.toLocaleString('en-US')}`;
};

const STATUS_LABEL = { free: 'Free', reserved: 'Reserved', seated: 'Seated', ordered: 'Ordered', bill: 'Wants bill', dirty: 'Clean up' };

class HomeCardsView {
  _layout = document.querySelector('.home-layout');
  _cols   = [document.querySelector('.home-left-col'), document.querySelector('.home-right-col')];
  _dash   = document.querySelector('.home-dash-card');
  _main   = [document.getElementById('homeChecklistCard'), document.getElementById('homeMyShiftCard')];
  _queue  = document.querySelector('.home-queue-panel');
  _slots  = [];
  _tiles  = new Map();
  _cards  = [];
  _area   = null;     // area shown on the Tables card
  _handlers = {};
  _picker = null;

  // handlers: { onSwapCard(key, btn), onSwapTile(key, btn), onTile(key), onAction(act, dataset) }
  init(handlers) {
    this._handlers = handlers;
    this._slots = [0, 1].map((i) => {
      const el = document.createElement('article');
      el.className = 'home-card home-slot-away';
      el.dataset.slot = String(i);
      el.addEventListener('click', (e) => this._onCardClick(e));
      this._cols[i]?.append(el);
      return el;
    });
    Object.entries(STATIC_TILES).forEach(([key, sel]) => {
      const el = document.querySelector(sel);
      if (!el) return;
      this._tiles.set(key, el);
      this._addTileSwap(el, key);
    });
    // Built-in cards carry their own swap button (index.html / myShiftView.js).
    this._layout?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-swap-card]');
      if (btn && !btn.closest('.home-card')) handlers.onSwapCard(btn.dataset.swapCard, btn);
    });
  }

  _addTileSwap(el, key) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'home-tile-swap';
    btn.setAttribute('aria-label', 'Swap this tile');
    btn.title = 'Choose what shows here';
    btn.innerHTML = SWAP_ICON;
    // The tile itself opens a report: the swap button must not.
    btn.addEventListener('click', (e) => { e.stopPropagation(); this._handlers.onSwapTile(key, btn); });
    btn.addEventListener('keydown', (e) => e.stopPropagation());
    el.append(btn);
  }

  _tile(key) {
    if (this._tiles.has(key)) return this._tiles.get(key);
    const look = TILE_LOOK[key];
    const label = TILES.find((t) => t.key === key)?.label ?? key;
    const el = document.createElement('div');
    el.className = `home-dash-stat home-dash-stat--extra home-dash-stat--${look.accent}`;
    el.dataset.tile = key;
    el.setAttribute('role', 'button');
    el.tabIndex = 0;
    el.title = look.title;
    el.innerHTML = `
      <span class="home-dash-emoji" aria-hidden="true">${look.emoji}</span>
      <div class="home-dash-body">
        <div class="home-dash-label">${label}</div>
        <div class="home-dash-value" data-tile-value>—</div>
        <div class="svs-base" data-tile-sub></div>
      </div>`;
    el.addEventListener('click', () => this._handlers.onTile(key));
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._handlers.onTile(key); }
    });
    this._addTileSwap(el, key);
    this._dash?.append(el);
    this._tiles.set(key, el);
    return el;
  }

  // ── Placement ─────────────────────────────────────────────────────────────
  apply({ cards, tiles }) {
    if (!this._slots.length) return; // before init(): the first apply after login does it
    this._cards = cards;
    [...this._main, this._queue, ...this._slots].forEach((el) => el?.classList.add('home-slot-away'));
    cards.forEach((key, i) => {
      const col = this._cols[i];
      if (!col) return;
      if (key === 'main') {
        this._main.forEach((el) => { if (el) { col.append(el); el.classList.remove('home-slot-away'); } });
      } else if (key === 'queue') {
        col.append(this._queue);
        this._queue.classList.remove('home-slot-away');
      } else {
        const slot = this._slots[i];
        if (slot.dataset.card !== key) {
          slot.dataset.card = key;
          slot.innerHTML = this._shell(key, this._cardLabel(key), '', '', '<p class="hc-empty">Loading…</p>');
        }
        col.append(slot);
        slot.classList.remove('home-slot-away');
      }
    });
    const shown = new Set(tiles);
    tiles.forEach((key, i) => {
      const el = this._tile(key);
      el.style.order = String(i);
      el.classList.remove('home-slot-away');
    });
    this._tiles.forEach((el, key) => { if (!shown.has(key)) el.classList.add('home-slot-away'); });
  }

  // Custom cards on show, so the controller only loads what is needed.
  customCards() { return this._cards.filter((k) => k !== 'main' && k !== 'queue'); }

  _cardLabel(key) { return CARDS.find((c) => c.key === key)?.label ?? key; }

  // ── Rendering ─────────────────────────────────────────────────────────────
  renderCards(data) {
    if (data.tz) TZ = data.tz;
    this._cards.forEach((key, i) => {
      if (key === 'main' || key === 'queue') return;
      const slot = this._slots[i];
      const body = slot.querySelector('.hc-body');
      const top = body?.scrollTop ?? 0;
      slot.innerHTML = this[`_${key}`](data);
      const nextBody = slot.querySelector('.hc-body');
      if (nextBody) nextBody.scrollTop = top;
    });
  }

  renderTiles(values) {
    this._tiles.forEach((el, key) => {
      if (STATIC_TILES[key] || !values[key]) return;
      el.querySelector('[data-tile-value]').textContent = values[key].value;
      el.querySelector('[data-tile-sub]').textContent = values[key].sub ?? '';
    });
  }

  _shell(key, title, sub, actions, body) {
    return `
      <div class="hc-head">
        <div class="hc-head-text">
          <h3 class="hc-title">${title}</h3>
          ${sub ? `<p class="hc-sub">${sub}</p>` : ''}
        </div>
        <div class="hc-actions">${actions}${swapButton(key)}</div>
      </div>
      <div class="hc-body">${body}</div>`;
  }

  _link(label, act, extra = '') {
    return `<button type="button" class="hc-link" data-hc-act="${act}"${extra}>${label}</button>`;
  }

  _tables({ tables: d }) {
    if (!d) return this._shell('tables', 'Tables', '', '', '<p class="hc-empty">Loading…</p>');
    if (!d.tables.length) {
      return this._shell('tables', 'Tables', '', '', `
        <div class="hc-empty-box">
          <p class="hc-empty">No tables yet.</p>
          ${this._link('Set up tables', 'open-tables')}
        </div>`);
    }
    const tabFor = (id) => d.tabs.find((t) => t.tableId === id) ?? null;
    const withStatus = d.tables.map((t) => ({ t, status: tableStatus(t, tabFor(t.id), d.reservations, d.now) }));
    const count = (...s) => withStatus.filter((x) => s.includes(x.status)).length;
    const chips = [
      ['free', 'Free', count('free', 'reserved')],
      ['seated', 'Seated', count('seated', 'ordered')],
      ['bill', 'Wants bill', count('bill')],
      ['dirty', 'Clean up', count('dirty')],
    ];
    const areas = [...new Set(d.tables.map((t) => t.area))];
    if (!areas.includes(this._area)) this._area = areas[0];
    const inArea = withStatus.filter((x) => x.t.area === this._area);
    // Crop the 1000 x 700 floor grid to this area's tables so they fill the card.
    const pad = 20;
    const x0 = Math.max(0, Math.min(...inArea.map(({ t }) => t.x)) - pad);
    const y0 = Math.max(0, Math.min(...inArea.map(({ t }) => t.y)) - pad);
    const x1 = Math.max(...inArea.map(({ t }) => t.x + t.w)) + pad;
    const y1 = Math.max(...inArea.map(({ t }) => t.y + t.h)) + pad;
    const bw = Math.max(1, x1 - x0), bh = Math.max(1, y1 - y0);
    const tableBtn = ({ t, status }) => {
      const tab = tabFor(t.id);
      // Small tables (bar seats) only fit their name; the color says the rest.
      const meta = tab ? `${tab.guests} · ${ago(tab.openedAt, d.now)}` : status === 'dirty' ? 'Clean up' : `${t.seats} ${t.seats === 1 ? 'seat' : 'seats'}`;
      const pos = `left:${(t.x - x0) / bw * 100}%;top:${(t.y - y0) / bh * 100}%;width:${t.w / bw * 100}%;height:${t.h / bh * 100}%`;
      return `
        <button type="button" class="hc-table hc-table--${status} hc-shape--${t.shape}" style="${pos}" data-hc-act="table" data-id="${t.id}" aria-label="Table ${esc(t.name)}, ${STATUS_LABEL[status]}">
          <b>${esc(t.name)}</b><span>${esc(meta)}</span>
        </button>`;
    };
    const areaTabs = areas.length > 1 ? `
      <div class="hc-areas" role="tablist" aria-label="Area">
        ${areas.map((a) => `<button type="button" role="tab" class="hc-area${a === this._area ? ' hc-area--on' : ''}" aria-selected="${a === this._area}" data-hc-act="area" data-area="${esc(a)}">${esc(a)}</button>`).join('')}
      </div>` : '';
    return this._shell('tables', 'Tables', 'Tap a table to open it', this._link('Full floor', 'open-tables'), `
      <div class="hc-counts">
        ${chips.map(([s, label, n]) => `<div class="hc-count hc-count--${s}"><b>${n}</b><span>${label}</span></div>`).join('')}
      </div>
      ${areaTabs}
      <div class="hc-floor-wrap">
        <div class="hc-floor" style="aspect-ratio:${bw} / ${bh}">${inArea.map(tableBtn).join('')}</div>
      </div>`);
  }

  _reservations({ tables: d }) {
    if (!d) return this._shell('reservations', "Today's reservations", '', '', '<p class="hc-empty">Loading…</p>');
    const list = d.reservations
      .filter((r) => r.status === 'booked' || r.status === 'seated')
      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
    const guests = list.reduce((s, r) => s + (r.partySize || 0), 0);
    const tableName = (id) => d.tables.find((t) => t.id === id)?.name ?? null;
    const busy = (id) => d.tabs.some((t) => t.tableId === id);
    const rows = list.map((r) => {
      const late = r.status === 'booked' && new Date(r.startsAt).getTime() < d.now;
      const name = tableName(r.tableId);
      let action;
      if (r.status === 'seated') action = '<span class="hc-done">Seated</span>';
      else if (r.tableId && !busy(r.tableId)) action = `<button type="button" class="hc-btn" data-hc-act="seat-res" data-id="${r.id}">Seat</button>`;
      else action = `<button type="button" class="hc-btn" data-hc-act="open-res" data-id="${r.id}">Open</button>`;
      return `
        <li class="hc-row${late ? ' hc-row--late' : ''}${r.status === 'seated' ? ' hc-row--muted' : ''}">
          <span class="hc-res-time">${clock(r.startsAt)}</span>
          <span class="hc-row-text">
            <b>${esc(r.name)} <span class="hc-row-meta">· ${plural(r.partySize, 'guest')}</span></b>
            ${r.note ? `<small>${esc(r.note)}</small>` : ''}
          </span>
          <span class="hc-chip">${name ? `Table ${esc(name)}` : 'No table'}</span>
          ${action}
        </li>`;
    }).join('');
    return this._shell('reservations', "Today's reservations",
      list.length ? `${plural(list.length, 'booking')} · ${plural(guests, 'guest')}` : '',
      this._link('Open', 'open-section', ' data-section="reservations"'),
      rows ? `<ul class="hc-list">${rows}</ul>` : '<p class="hc-empty">No bookings today.</p>');
  }

  _waitlist({ tables: d }) {
    if (!d) return this._shell('waitlist', 'Waitlist', '', '', '<p class="hc-empty">Loading…</p>');
    const waiting = d.waitlist.filter((w) => w.status === 'waiting');
    const free = d.tables.filter((t) => tableStatus(t, d.tabs.find((x) => x.tableId === t.id), d.reservations, d.now) === 'free');
    const rows = waiting.map((w) => {
      const waited = mins(d.now - new Date(w.addedAt).getTime());
      const over = w.quotedMin != null && waited > w.quotedMin;
      const fit = free.filter((t) => t.seats >= w.partySize).sort((a, b) => a.seats - b.seats)[0];
      const action = fit
        ? `<button type="button" class="hc-btn" data-hc-act="seat-wait" data-id="${w.id}" data-table="${fit.id}">Seat at ${esc(fit.name)}</button>`
        : `<button type="button" class="hc-btn" data-hc-act="open-section" data-section="waitlist">Open</button>`;
      return `
        <li class="hc-row">
          <span class="hc-party" aria-label="Party of ${w.partySize}"><b>${w.partySize}</b><small>guests</small></span>
          <span class="hc-row-text"><b>${esc(w.name)}</b>${w.note ? `<small>${esc(w.note)}</small>` : ''}</span>
          <span class="hc-wait${over ? ' hc-wait--over' : ''}"><b>${waited} min</b><small>${w.quotedMin != null ? `told ${w.quotedMin} min` : 'waiting'}</small></span>
          ${action}
        </li>`;
    }).join('');
    return this._shell('waitlist', 'Waitlist',
      waiting.length ? `${waiting.length} waiting · ${plural(free.length, 'free table')}` : '',
      `<button type="button" class="hc-btn hc-btn--pri" data-hc-act="open-section" data-section="waitlist">Add a party</button>`,
      rows ? `<ul class="hc-list">${rows}</ul>` : '<p class="hc-empty">Nobody is waiting.</p>');
  }

  _team({ onShift, now }) {
    if (!onShift) return this._shell('team', "Who's working", '', '', '<p class="hc-empty">Loading…</p>');
    const list = [...onShift].sort((a, b) => new Date(a.clockedInAt) - new Date(b.clockedInAt));
    const rows = list.map((p) => `
      <li class="hc-row">
        <span class="hc-avatar" aria-hidden="true">${esc(initials(p.name))}</span>
        <span class="hc-row-text"><b>${esc(p.name || 'Staff member')}</b><small>In since ${clock(p.clockedInAt)} · ${ago(p.clockedInAt, now)}</small></span>
        ${p.onBreak ? '<span class="hc-chip hc-chip--break">On break</span>' : '<span class="hc-chip hc-chip--on">Working</span>'}
      </li>`).join('');
    return this._shell('team', "Who's working", list.length ? `${list.length} clocked in` : '', '',
      rows ? `<ul class="hc-list">${rows}</ul>` : '<p class="hc-empty">Nobody is clocked in yet.</p>');
  }

  _topSellers({ top }) {
    if (!top) return this._shell('topSellers', 'Top sellers today', '', '', '<p class="hc-empty">Loading…</p>');
    const max = Math.max(1, ...top.map((t) => t.quantity));
    const rows = top.map((t, i) => `
      <li class="hc-rank">
        <span class="hc-rank-n">${i + 1}</span>
        <span class="hc-rank-text">
          <span class="hc-rank-top"><b>${esc(t.name)}</b><span>${t.quantity} sold</span></span>
          <span class="hc-bar"><i style="width:${Math.max(4, t.quantity / max * 100)}%"></i></span>
        </span>
      </li>`).join('');
    return this._shell('topSellers', 'Top sellers today', top.length ? 'By items sold' : '', '',
      rows ? `<ol class="hc-list">${rows}</ol>` : '<p class="hc-empty">No sales yet today.</p>');
  }

  _lowStock({ lowStock, canInventory }) {
    if (!lowStock) return this._shell('lowStock', 'Low stock', '', '', '<p class="hc-empty">Loading…</p>');
    const rows = lowStock.map((it) => {
      const inner = `
        <span class="hc-row-text"><b>${esc(it.name)}</b><small>${esc(`${it.quantity} ${it.unit ?? ''}`.trim())} left</small></span>
        <span class="hc-chip ${it.out ? 'hc-chip--out' : 'hc-chip--low'}">${it.out ? 'Out' : 'Low'}</span>`;
      return canInventory
        ? `<li><button type="button" class="hc-row hc-row--btn" data-hc-act="inventory">${inner}</button></li>`
        : `<li class="hc-row">${inner}</li>`;
    }).join('');
    return this._shell('lowStock', 'Low stock', lowStock.length ? plural(lowStock.length, 'item') : '',
      canInventory ? this._link('Inventory', 'inventory') : '',
      rows ? `<ul class="hc-list">${rows}</ul>` : '<p class="hc-empty">Nothing is running low.</p>');
  }

  // "+12% vs ..." line under a sales card title.
  _trend(now, before, vsLabel) {
    if (!before) return now > 0 ? `<span class="hc-trend hc-trend--up">New</span> ${vsLabel}` : '';
    const pct = Math.round((now - before) / before * 100);
    const up = pct >= 0;
    return `<span class="hc-trend ${up ? 'hc-trend--up' : 'hc-trend--down'}">${up ? '▲' : '▼'} ${Math.abs(pct)}%</span> ${vsLabel}`;
  }

  _salesToday({ salesTrend: t }) {
    if (!t) return this._shell('salesToday', 'Sales today', '', '', '<p class="hc-empty">Loading…</p>');
    const max = Math.max(1, ...t.hours.map((h) => Math.max(h.today, h.yday)));
    const bars = t.hours.map((h) => `
      <div class="hc-col${h.now ? ' hc-col--now' : ''}" title="${h.label}: ${formatMoney(h.today)} (yesterday ${formatMoney(h.yday)})">
        <div class="hc-col-bars">
          <i class="hc-col-ghost" style="height:${h.yday / max * 100}%"></i>
          ${h.future ? '' : `<i class="hc-col-fill" style="height:${Math.max(h.today ? 3 : 0, h.today / max * 100)}%"></i>`}
        </div>
        <span class="hc-col-label">${h.showLabel ? h.label : ''}</span>
      </div>`).join('');
    const best = t.hours.filter((h) => !h.future && h.today > 0).sort((a, b) => b.today - a.today)[0];
    return this._shell('salesToday', 'Sales today',
      `<b class="hc-big">${formatMoney(t.total)}</b> ${this._trend(t.total, t.ydaySoFar, 'vs this time yesterday')}`,
      this._link('Reports', 'reports'), `
      <div class="hc-chart" role="img" aria-label="Sales by hour today, ${formatMoney(t.total)} so far">${bars}</div>
      <div class="hc-legend">
        <span><i class="hc-key hc-key--today"></i>Today</span>
        <span><i class="hc-key hc-key--ghost"></i>Yesterday</span>
        ${best ? `<span class="hc-legend-note">Busiest so far: ${best.label}</span>` : ''}
      </div>`);
  }

  _salesWeek({ salesTrend: t }) {
    if (!t) return this._shell('salesWeek', 'Last 7 days', '', '', '<p class="hc-empty">Loading…</p>');
    const max = Math.max(1, ...t.days.map((d) => Math.max(d.value, d.prev)));
    const best = [...t.days].sort((a, b) => b.value - a.value)[0];
    const bars = t.days.map((d) => `
      <div class="hc-col hc-col--day${d.today ? ' hc-col--now' : ''}" title="${d.label}: ${formatMoney(d.value)} (week before ${formatMoney(d.prev)})">
        <span class="hc-col-value">${d.value ? shortMoney(d.value) : ''}</span>
        <div class="hc-col-bars">
          <i class="hc-col-ghost" style="height:${d.prev / max * 100}%"></i>
          <i class="hc-col-fill" style="height:${Math.max(d.value ? 3 : 0, d.value / max * 100)}%"></i>
        </div>
        <span class="hc-col-label">${d.short}</span>
      </div>`).join('');
    return this._shell('salesWeek', 'Last 7 days',
      `<b class="hc-big">${formatMoney(t.weekTotal)}</b> ${this._trend(t.weekTotal, t.prevWeekTotal, 'vs the 7 days before')}`,
      this._link('Reports', 'reports'), `
      <div class="hc-chart hc-chart--week" role="img" aria-label="Sales for the last 7 days, ${formatMoney(t.weekTotal)}">${bars}</div>
      <div class="hc-legend">
        <span><i class="hc-key hc-key--today"></i>This week</span>
        <span><i class="hc-key hc-key--ghost"></i>Week before</span>
        ${best?.value ? `<span class="hc-legend-note">Best day: ${best.label}</span>` : ''}
      </div>`);
  }

  _schedule({ schedule: s }) {
    if (!s) return this._shell('schedule', "Today's schedule", '', '', '<p class="hc-empty">Loading…</p>');
    const chip = {
      working: '<span class="hc-chip hc-chip--on">Working</span>',
      break:   '<span class="hc-chip hc-chip--break">On break</span>',
      late:    '<span class="hc-chip hc-chip--late">Not in yet</span>',
      done:    '<span class="hc-chip">Done</span>',
      open:    '<span class="hc-chip hc-chip--open">Open shift</span>',
    };
    const rows = s.rows.map((r) => `
      <li class="hc-row hc-row--shift${r.state === 'done' ? ' hc-row--muted' : ''}" style="--station:${esc(r.color || '#64748B')}">
        <span class="hc-shift-bar" aria-hidden="true"></span>
        <span class="hc-row-text">
          <b>${r.open ? 'Open shift' : esc(r.name)}</b>
          <small>${r.time}${r.station ? ` · ${esc(r.station)}` : ''}</small>
        </span>
        ${chip[r.state] ?? `<span class="hc-chip">${esc(r.startsIn)}</span>`}
      </li>`).join('');
    const late = s.rows.filter((r) => r.state === 'late').length;
    return this._shell('schedule', "Today's schedule",
      s.rows.length ? `${plural(s.rows.length, 'shift')}${late ? ` · <span class="hc-warn">${late} not in yet</span>` : ''}` : '',
      s.canOpen ? this._link('Schedule', 'schedule') : '',
      rows ? `<ul class="hc-list">${rows}</ul>` : '<p class="hc-empty">No shifts scheduled today.</p>');
  }

  _inbox({ inbox }) {
    if (!inbox) return this._shell('inbox', 'Needs your OK', '', '', '<p class="hc-empty">Loading…</p>');
    const total = inbox.reduce((s, r) => s + r.count, 0);
    const rows = inbox.filter((r) => r.count > 0).map((r) => `
      <li><button type="button" class="hc-row hc-row--btn" data-hc-act="inbox" data-cat="${r.cat}">
        <span class="hc-inbox-icon hc-inbox-icon--${r.cat}" aria-hidden="true">${svg(r.icon, 18)}</span>
        <span class="hc-row-text"><b>${r.label}</b><small>${r.sub}</small></span>
        <span class="hc-count-badge">${r.count}</span>
      </button></li>`).join('');
    return this._shell('inbox', 'Needs your OK', total ? `${plural(total, 'thing')} waiting for you` : '', '',
      rows ? `<ul class="hc-list">${rows}</ul>` : `
        <div class="hc-empty-box">
          <span class="hc-done-icon" aria-hidden="true">${svg('<path d="M20 6 9 17l-5-5"/>', 22)}</span>
          <p class="hc-empty">All caught up. Nothing is waiting for you.</p>
        </div>`);
  }

  _purchaseOrders({ pos }) {
    if (!pos) return this._shell('purchaseOrders', 'Purchase orders', '', '', '<p class="hc-empty">Loading…</p>');
    const rows = pos.open.map((p) => `
      <li><button type="button" class="hc-row hc-row--btn" data-hc-act="po" data-id="${p.id}">
        <span class="hc-row-text">
          <b>${p.label}${p.supplier ? ` <span class="hc-row-meta">· ${esc(p.supplier)}</span>` : ''}</b>
          <small>${plural(p.lines, 'item')} · ${formatMoney(p.total)}</small>
        </span>
        <span class="hc-chip${p.overdue ? ' hc-chip--late' : ''}">${esc(p.when)}</span>
      </button></li>`).join('');
    return this._shell('purchaseOrders', 'Purchase orders',
      pos.open.length ? `${plural(pos.open.length, 'order')} on the way` : '',
      this._link('Inventory', 'inventory'), `
      ${rows ? `<ul class="hc-list">${rows}</ul>` : '<p class="hc-empty">No orders on the way.</p>'}
      ${pos.drafts ? `<button type="button" class="hc-note" data-hc-act="inbox" data-cat="po">${plural(pos.drafts, 'draft')} not sent yet</button>` : ''}`);
  }

  _onCardClick(e) {
    const swap = e.target.closest('[data-swap-card]');
    if (swap) { this._handlers.onSwapCard(swap.dataset.swapCard, swap); return; }
    const btn = e.target.closest('[data-hc-act]');
    if (!btn) return;
    if (btn.dataset.hcAct === 'area') {
      this._area = btn.dataset.area;
      this._handlers.onAction('rerender', btn.dataset);
      return;
    }
    this._handlers.onAction(btn.dataset.hcAct, btn.dataset);
  }

  // ── Picker ────────────────────────────────────────────────────────────────
  // options: [{ key, label, desc?, value?, group?, selected, disabled, tag? }]
  openPicker({ anchor, kind, title, sub, options, onPick, onSuggested }) {
    this.closePicker();
    const scrim = document.createElement('div');
    scrim.className = 'hc-picker-scrim';
    const box = document.createElement('section');
    box.className = `hc-picker hc-picker--${kind}`;
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-label', title);
    const optionBtn = (o) => `
      <button type="button" class="hc-opt${o.selected ? ' hc-opt--on' : ''}" data-pick="${o.key}"${o.disabled ? ' disabled aria-disabled="true"' : ''} aria-pressed="${o.selected}">
        ${kind === 'card' ? `<span class="hc-opt-icon">${svg(CARD_ICONS[o.key] ?? '')}</span>` : ''}
        <span class="hc-opt-text">
          <span class="hc-opt-label">${esc(o.label)}</span>
          ${o.value != null ? `<span class="hc-opt-value">${esc(o.value)}</span>` : ''}
          ${o.desc ? `<span class="hc-opt-desc">${esc(o.desc)}</span>` : ''}
        </span>
        ${o.tag ? `<span class="hc-opt-tag${o.tag === 'Pro' ? ' hc-opt-tag--pro' : ''}">${esc(o.tag)}</span>` : ''}
        ${o.selected ? `<span class="hc-opt-check" aria-hidden="true">${svg('<path d="M20 6 9 17l-5-5"/>', 18)}</span>` : ''}
      </button>`;
    const groups = [...new Set(options.map((o) => o.group))];
    const list = groups.map((g) => `
      <div class="hc-opt-group">
        <p class="hc-opt-group-name">${esc(g)}</p>
        <div class="${kind === 'tile' ? 'hc-opt-grid' : 'hc-opt-list'}">${options.filter((o) => o.group === g).map(optionBtn).join('')}</div>
      </div>`).join('');
    box.innerHTML = `
      <div class="hc-picker-head">
        <div>
          <h2 class="hc-picker-title">${esc(title)}</h2>
          <p class="hc-picker-sub">${esc(sub)}</p>
        </div>
        <button type="button" class="hc-picker-close" data-close aria-label="Close">&times;</button>
      </div>
      <div class="hc-picker-body">${list}</div>
      <div class="hc-picker-foot">
        <span>${kind === 'tile' ? "Picking one that's already showing swaps the two." : 'Only changes this device. Nothing is turned off.'}</span>
        <button type="button" class="hc-link" data-suggest>Use suggested</button>
      </div>`;
    document.body.append(scrim, box);
    this._picker = { scrim, box, anchor };
    this._placePicker();

    const close = () => this.closePicker(true);
    scrim.addEventListener('click', close);
    box.addEventListener('click', (e) => {
      if (e.target.closest('[data-close]')) return close();
      if (e.target.closest('[data-suggest]')) { this.closePicker(true); onSuggested(); return; }
      const opt = e.target.closest('[data-pick]');
      if (opt && !opt.disabled) { this.closePicker(true); onPick(opt.dataset.pick); }
    });
    box.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } });
    this._onResize = () => this._placePicker();
    window.addEventListener('resize', this._onResize);
    (box.querySelector('.hc-opt--on') ?? box.querySelector('.hc-opt:not([disabled])'))?.focus();
  }

  _placePicker() {
    const p = this._picker;
    if (!p) return;
    const vw = window.innerWidth, vh = window.innerHeight;
    if (vw <= 600) { p.box.classList.add('hc-picker--sheet'); p.box.style.cssText = ''; return; }
    p.box.classList.remove('hc-picker--sheet');
    const r = p.anchor.getBoundingClientRect();
    const w = p.box.offsetWidth, h = p.box.offsetHeight;
    const left = Math.min(Math.max(12, r.right - w), vw - w - 12);
    let top = r.bottom + 8;
    if (top + h > vh - 12) top = Math.max(12, vh - h - 12);
    p.box.style.left = `${left}px`;
    p.box.style.top = `${top}px`;
  }

  closePicker(restoreFocus = false) {
    const p = this._picker;
    if (!p) return;
    p.scrim.remove();
    p.box.remove();
    window.removeEventListener('resize', this._onResize);
    this._picker = null;
    if (restoreFocus && p.anchor.isConnected) p.anchor.focus();
  }
}

export default new HomeCardsView();
